import React from 'react';
import { Box, Container, Typography, Grid, Link } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import { Facebook, Twitter, LinkedIn, Instagram } from '@mui/icons-material';
import { images, getImageUrl } from '../utils/imageUtils';

const FooterSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#000',
  color: '#fff',
  padding: '60px 0 30px',
  borderTop: 'none',
}));

const FooterLogo = styled(Typography)(({ theme }) => ({
  color: '#fff',
  fontSize: '1.35rem',
  fontWeight: 700,
  marginBottom: '6px',
  textAlign: 'center',
  letterSpacing: 0.5,
}));

const FooterLogoImg = styled('img')(() => ({
  height: 90,
  width: 'auto',
  borderRadius: 12,
  boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
  margin: '0 auto 10px auto',
  display: 'block',
}));

const FooterDesc = styled(Typography)(({ theme }) => ({
  color: '#fff',
  fontSize: '1.05rem',
  textAlign: 'center',
  marginBottom: 10,
  marginTop: 4,
  opacity: 0.85,
}));

const FooterLinks = styled(Box)(({ theme }) => ({
  '& h3': {
    color: '#ffd700',
    marginBottom: '20px',
    fontSize: '1.2rem',
    fontWeight: 700,
    letterSpacing: 1,
    textAlign: 'left',
  },
  '& ul': {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  '& li': {
    marginBottom: '14px',
  },
  '& a': {
    color: '#f8f9fa',
    textDecoration: 'none',
    fontSize: '1.05rem',
    fontWeight: 500,
    letterSpacing: 0.5,
    transition: 'color 0.3s, transform 0.2s',
    '&:hover': {
      color: '#ffd700',
      transform: 'translateX(4px) scale(1.05)',
    },
  },
}));

const SocialIcons = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: '1.5rem',
  marginTop: 18,
  marginBottom: 8,
  '& svg': {
    color: '#ffd700',
    fontSize: 34,
    transition: 'transform 0.2s, color 0.2s',
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.primary.light,
      transform: 'scale(1.18)',
    },
  },
}));

const Copyright = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  paddingTop: '30px',
  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  marginTop: 24,
  fontSize: '1rem',
  color: '#bdbdbd',
}));

const FooterGrid = styled(Grid)(({ theme }) => ({
  alignItems: 'flex-start',
  [theme.breakpoints.down('md')]: {
    textAlign: 'center',
    justifyContent: 'center',
  },
}));

const SubscribeBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
  width: '100%',
  maxWidth: 320,
  margin: '0 auto',
}));

const EmailInput = styled('input')(({ theme }) => ({
  width: '100%',
  padding: '10px 14px',
  borderRadius: 6,
  border: 'none',
  fontSize: '1rem',
  marginBottom: theme.spacing(1),
  outline: 'none',
  background: '#181818',
  color: '#fff',
}));

const SendButton = styled('button')(({ theme }) => ({
  width: '100%',
  padding: '10px 0',
  borderRadius: 6,
  border: 'none',
  background: '#222',
  color: '#fff',
  fontWeight: 700,
  fontSize: '1rem',
  cursor: 'pointer',
  transition: 'background 0.2s',
  '&:hover': {
    background: '#333',
  },
}));

const Footer = () => {
  const { t } = useTranslation();

  const navItems = [
    { key: 'home', label: t('home'), to: '#home' },
    { key: 'about', label: t('about.label'), to: '#about' },
    { key: 'services', label: t('services.label'), to: '#services' },
    { key: 'routes', label: t('routes.label'), to: '#routes' },
    { key: 'contact', label: t('contact.label'), to: '#contact' },
  ];

  const services = [
    { text: t('footer.marineCargo'), to: '#' },
    { text: t('footer.containerShipping'), to: '#' },
    { text: t('footer.bulkCargo'), to: '#' },
    { text: t('footer.doorToDoor'), to: '#' },
    { text: t('footer.customsClearance'), to: '#' },
  ];

  const regions = [
    { text: t('footer.somalia'), to: '#routes-somalia' },
    { text: t('footer.yemen'), to: '#routes-yemen' },
    { text: t('footer.oman'), to: '#routes-oman' },
    { text: t('footer.uae'), to: '#routes-uae' },
    { text: t('footer.pakistan'), to: '#routes-pakistan' },
    { text: t('footer.india'), to: '#routes-india' },
  ];

  return (
    <FooterSection>
      <Container>
        <FooterGrid container spacing={4}>
          <Grid item xs={12} md={3} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <FooterLogoImg 
              src={getImageUrl(images.logoDark2)}
              alt="Gobaad Logistics Logo"
              onError={(e) => {
                console.error('Error loading image:', e);
                e.target.onerror = null;
                e.target.src = getImageUrl(images.logoDark2);
              }}
            />
            <FooterLogo>Gobaad Logistics and Trading</FooterLogo>
          </Grid>
          <Grid item xs={12} md={3}>
            <FooterLinks>
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, mb: 2 }}>Quick Links</Typography>
              <ul>
                {navItems.map((item, index) => (
                  <li key={item.key}>
                    <Link href={item.to} sx={{ color: '#fff', opacity: 0.9, fontWeight: 400, fontSize: '1.05rem', textDecoration: 'none', display: 'block', py: 0.5 }}>
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </FooterLinks>
          </Grid>
          <Grid item xs={12} md={3}>
            <FooterLinks>
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, mb: 2 }}>Get In Touch</Typography>
              <Typography sx={{ color: '#fff', opacity: 0.85, mb: 1 }}>Mudug - Puntland Somalia</Typography>
              <Link href="mailto:info@gobaadlogistics.com" sx={{ color: '#fff', opacity: 0.9, fontWeight: 400, fontSize: '1.05rem', textDecoration: 'none', display: 'block', mb: 1 }}>
                info@gobaadlogistics.com
              </Link>
              <Box sx={{ mt: 2, mb: 1, color: '#fff', fontWeight: 500, textAlign: 'left' }}>
                <span>Follow Us On</span>
                <SocialIcons>
                  <Facebook />
                  <Twitter />
                  <LinkedIn />
                  <Instagram />
                </SocialIcons>
              </Box>
            </FooterLinks>
          </Grid>
          <Grid item xs={12} md={3}>
            <FooterLinks>
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, mb: 2 }}>Subscribe For Updates</Typography>
              <Typography sx={{ color: '#fff', opacity: 0.85, mb: 1 }}>Get updates for news and events</Typography>
              <SubscribeBox>
                <EmailInput type="email" placeholder="Email" />
                <SendButton>Send</SendButton>
              </SubscribeBox>
            </FooterLinks>
          </Grid>
        </FooterGrid>
        <Copyright>
          <Typography>
            Copyright © 2025 – All Rights Reserved.
          </Typography>
        </Copyright>
      </Container>
    </FooterSection>
  );
};

export default Footer; 